import { create } from 'zustand';
import type { CameraMode } from '../engine/CameraRig';
import type { QualityLevel } from '../engine/quality';
import { DEFAULT_SETTINGS, loadSettings, saveSettings, type ControlMode, type GameMode, type Settings } from '../save/settings';
import { DEFAULT_PROGRESS, loadProgress, saveProgress, type GameProgress } from '../save/progress';

export type GamePhase = 'loading' | 'home' | 'playing' | 'paused' | 'settings';

interface GameStore extends Settings {
  phase: GamePhase;
  ready: boolean;
  setPhase: (phase: GamePhase) => void;
  setReady: (ready: boolean) => void;
  setControlMode: (mode: ControlMode) => void;
  setGameMode: (mode: GameMode) => void;
  setQuality: (quality: QualityLevel, auto?: boolean) => void;
  setVehicle: (id: string) => void;
  setPaintColor: (color: number) => void;
  setCameraMode: (mode: CameraMode) => void;
  toggleVibration: () => void;
  toggleSound: () => void;
  togglePerf: () => void;
  setGyroSensitivity: (value: number) => void;
  progressLoaded: boolean;
  coins: number;
  stars: number;
  collected: string[];
  missionStars: Record<string, number>;
  unlockedVehicles: string[];
  hydrateProgress: () => Promise<void>;
  collect: (id: string) => void;
  completeMission: (id: string, rewardCoins: number, rewardStars: number) => void;
  replaceProgress: (progress: GameProgress) => Promise<boolean>;
}

const initial =
  typeof window === 'undefined' ? DEFAULT_SETTINGS : withUrlOverrides(loadSettings());

/**
 * `?perf=1` blendet den Performance-Overlay sofort ein, `?quality=low|medium|high`
 * erzwingt ein Profil. Nur zum Messen gedacht - im normalen Spiel fuehrt kein
 * Weg dorthin ausser ueber die versteckte Geste im Pausenmenue.
 */
function withUrlOverrides(settings: Settings): Settings {
  const params = new URLSearchParams(window.location.search);
  const next = { ...settings };
  if (params.has('perf')) next.showPerf = params.get('perf') !== '0';
  const q = params.get('quality');
  if (q === 'low' || q === 'medium' || q === 'high') {
    next.quality = q;
    next.qualityAuto = false;
  }
  return next;
}

/**
 * Menue- und Einstellungszustand. Bewusst NUR fuer Dinge, die sich selten
 * aendern - Tacho, Drehzahl und Performance-Werte laufen ueber Refs
 * (siehe src/ui/telemetry.ts) und loesen kein Rendern aus.
 */
export const useGameStore = create<GameStore>()((set, get) => {
  const persist = (): void => {
    const s = get();
    saveSettings({
      controlMode: s.controlMode,
      gameMode: s.gameMode,
      quality: s.quality,
      qualityAuto: s.qualityAuto,
      vehicleId: s.vehicleId,
      paintColor: s.paintColor,
      cameraMode: s.cameraMode,
      vibration: s.vibration,
      sound: s.sound,
      showPerf: s.showPerf,
      gyroSensitivity: s.gyroSensitivity,
    });
  };

  return {
    ...initial,
    phase: 'loading',
    ready: false,
    ...DEFAULT_PROGRESS,
    progressLoaded: false,
    setPhase: (phase) => set({ phase }),
    setReady: (ready) => set({ ready }),
    setControlMode: (controlMode) => {
      set({ controlMode });
      persist();
    },
    setGameMode: (gameMode) => {
      set({ gameMode });
      persist();
    },
    setQuality: (quality, auto = false) => {
      set({ quality, qualityAuto: auto });
      persist();
    },
    setVehicle: (vehicleId) => {
      set({ vehicleId });
      persist();
    },
    setPaintColor: (paintColor) => {
      set({ paintColor });
      persist();
    },
    setCameraMode: (cameraMode) => {
      set({ cameraMode });
      persist();
    },
    toggleVibration: () => {
      set({ vibration: !get().vibration });
      persist();
    },
    toggleSound: () => {
      set({ sound: !get().sound });
      persist();
    },
    togglePerf: () => {
      set({ showPerf: !get().showPerf });
      persist();
    },
    setGyroSensitivity: (gyroSensitivity) => {
      set({ gyroSensitivity });
      persist();
    },
    hydrateProgress: async () => {
      const progress = await loadProgress();
      set({ ...progress, progressLoaded: true });
    },
    collect: (id) => {
      const current = get();
      if (current.collected.includes(id)) return;
      const progress: GameProgress = {
        version: 1,
        coins: current.coins + 5,
        stars: current.stars,
        collected: [...current.collected, id],
        missionStars: current.missionStars,
        unlockedVehicles: current.unlockedVehicles,
      };
      set(progress);
      void saveProgress(progress);
    },
    completeMission: (id, rewardCoins, rewardStars) => {
      const current = get();
      const previousStars = current.missionStars[id] ?? 0;
      const newStars = Math.max(previousStars, rewardStars);
      const starGain = newStars - previousStars;
      const unlocks = new Set(current.unlockedVehicles);
      if (current.stars + starGain >= 3) unlocks.add('muldenkipper');
      if (current.stars + starGain >= 6) unlocks.add('sportcoupe');
      const progress: GameProgress = {
        version: 1,
        coins: current.coins + rewardCoins,
        stars: current.stars + starGain,
        collected: current.collected,
        missionStars: { ...current.missionStars, [id]: newStars },
        unlockedVehicles: [...unlocks],
      };
      set(progress);
      void saveProgress(progress);
    },
    replaceProgress: async (progress) => {
      const persisted = await saveProgress(progress);
      if (persisted) set({ ...progress, progressLoaded: true });
      return persisted;
    },
  };
});
